import React, { useMemo, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    CircularProgress,
    IconButton,
    Button,
    Collapse,
    Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import FolderIcon from '@mui/icons-material/Folder';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { useHistory } from 'react-router-dom';
import { useReports } from './useReports';

const TILE_MIN_WIDTH = 250;

const Reports = () => {
    const history = useHistory();

    const {
        groupedReports,
        loading,
        searchText,
        setSearchText,
        clearSearch,
    } = useReports();

    const groupNames = useMemo(() => Object.keys(groupedReports), [groupedReports]);

    const isSearching = !!searchText.trim();

    // Track collapsed folders. Default: empty => every folder is open (grid keeps it compact).
    const [collapsed, setCollapsed] = useState({});

    const isGroupOpen = (name) => isSearching || !collapsed[name];

    const toggleGroup = (name) => {
        if (isSearching) return; // while searching, everything stays open
        setCollapsed((prev) => ({ ...prev, [name]: !prev[name] }));
    };

    const collapseAll = () =>
        setCollapsed(groupNames.reduce((acc, name) => ({ ...acc, [name]: true }), {}));

    const expandAll = () => setCollapsed({});

    const allCollapsed =
        groupNames.length > 0 && groupNames.every((name) => collapsed[name]);

    const handleOpenReport = (item) => {
        history.push(`/powerBIReports/${item.reportID}`, {
            reportID: item.reportID,
            varID: item.varID,
            reportName: item.subMenuName,
        });
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                bgcolor: 'background.paper',
            }}
        >
            <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h5" fontWeight={600}>
                    Reports
                </Typography>
                <Button variant="contained" onClick={() => history.push('/')}>
                    Home
                </Button>
            </Box>

            <Box sx={{ px: 3, pb: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by report name..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: searchText ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={clearSearch}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : undefined,
                    }}
                />
            </Box>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                </Box>
            )}

            {!loading && groupNames.length === 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: 1,
                        px: 3,
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="subtitle1" fontWeight={600}>
                        No Report Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Reports added in Power BI will be shown here.
                    </Typography>
                </Box>
            )}

            {!loading && groupNames.length > 0 && (
                <Box sx={{ overflowY: 'auto', flex: 1, px: 3, pb: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1,
                            minHeight: 30,
                        }}
                    >
                        <Typography variant="body2" fontWeight={600}>
                            Select a report:
                        </Typography>
                        {!isSearching && (
                            <Button
                                size="small"
                                startIcon={
                                    allCollapsed
                                        ? <UnfoldMoreIcon fontSize="small" />
                                        : <UnfoldLessIcon fontSize="small" />
                                }
                                onClick={allCollapsed ? expandAll : collapseAll}
                            >
                                {allCollapsed ? 'Expand all' : 'Collapse all'}
                            </Button>
                        )}
                    </Box>

                    {groupNames.map((groupName) => {
                        const items = groupedReports[groupName];
                        const isOpen = isGroupOpen(groupName);

                        return (
                            <Box key={groupName} sx={{ mb: 1 }}>
                                {/* Folder header — sticky so the group name stays visible while scrolling */}
                                <Box
                                    sx={{
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 2,
                                        bgcolor: 'background.paper',
                                        pb: 0.5,
                                    }}
                                >
                                    <Box
                                        onClick={() => toggleGroup(groupName)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            height: 36,
                                            cursor: isSearching ? 'default' : 'pointer',
                                            borderRadius: 1,
                                            px: 0.5,
                                            bgcolor: 'action.hover',
                                            '&:hover': {
                                                bgcolor: isSearching ? 'action.hover' : 'action.selected',
                                            },
                                        }}
                                    >
                                        <KeyboardArrowRightIcon
                                            fontSize="small"
                                            sx={{
                                                transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.15s ease',
                                                color: 'action.active',
                                            }}
                                        />
                                        <FolderIcon fontSize="small" sx={{ color: 'primary.main', mx: 0.75 }} />
                                        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }} noWrap>
                                            {groupName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ pr: 1 }}>
                                            {items.length}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Reports as a horizontal, wrapping grid of tiles */}
                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: `repeat(auto-fill, minmax(${TILE_MIN_WIDTH}px, 1fr))`,
                                            gap: 1,
                                            pt: 0.5,
                                            pb: 1,
                                        }}
                                    >
                                        {items.map((item) => (
                                            <Tooltip key={item.varID} title={item.subMenuName} enterDelay={600}>
                                                <Box
                                                    onClick={() => handleOpenReport(item)}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        minHeight: 48,
                                                        px: 1.5,
                                                        py: 1,
                                                        cursor: 'pointer',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        borderRadius: 1.5,
                                                        transition: 'all 0.15s ease',
                                                        '&:hover': {
                                                            bgcolor: 'action.hover',
                                                            borderColor: 'primary.main',
                                                            boxShadow: 1,
                                                        },
                                                    }}
                                                >
                                                    <AssessmentOutlinedIcon
                                                        fontSize="small"
                                                        sx={{ color: 'primary.main', flexShrink: 0 }}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            lineHeight: 1.3,
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            wordBreak: 'break-word',
                                                        }}
                                                    >
                                                        {item.subMenuName}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        ))}
                                    </Box>
                                </Collapse>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

export default Reports;